import styled from '@emotion/styled'

import { LIST_SIZE, type CustomAnalyticsAttributes } from '@/blocks/constants'
import { Button } from '@/components/Button'
import {
  Container,
  type BackgroundColor,
  type ContainerProps,
  type Space,
} from '@/components/Container'
import { Countdown } from '@/components/Countdown'
import type { HasCountdownProps } from '@/components/Countdown/types'
import { Media, type MediaProps } from '@/components/Media'
import {
  BodyM,
  Display,
  HeadingL,
  SubHeading,
  type TypographyColorModifier,
} from '@/components/Typography'
import { extractCustomButtonEvent } from '@/core/analytics/utils/extract-custom-event-props'
import { parseIsoToTimestampMS } from '@/core/utils/date/parseIsoToTimestampMS'
import { decodeURL } from '@/core/utils/dom/decodeURL'
import type { BlockButtonProps, TitleProps } from '@/models'
import { CONFIG } from '@/styles/emotion.config'

import { builderLightText } from '../utils'

const HeroProductBlockStyles = styled.div({
  '& .e': {
    marginTop: '0',
    width: '100%',
  },

  /* container */
  '& .a': {
    display: 'grid',
    gridTemplateColumns: '1fr 25rem',
    gridTemplateAreas: '"title image" "button image"',
    marginTop: '-0.5rem',
  },

  /* title container */
  '& .b': {
    gridArea: 'title',
    marginRight: '2.5rem',
    alignSelf: 'flex-end',
  },

  /* button */
  '& .c': {
    gridArea: 'button',
    width: '20rem',
  },

  /* media container */
  '& .d': {
    position: 'relative',
    width: '100%',
    height: '19.75rem',
    maxWidth: '25rem',
    gridArea: 'image',
    borderRadius: 'var(--radii-default)',
    overflow: 'hidden',
  },

  '& .d img': {
    width: '100%',
    height: '100%',
  },

  [CONFIG.media.mobileLg]: {
    '.a': {
      display: 'grid',
      gridTemplateColumns: '1fr',
      alignItems: 'flex-start',
      justifyContent: 'center',
      margin: '0 auto',
      gridTemplateAreas: '"title" "image" "button"',
    },

    '.b': {
      marginRight: '0',
    },

    '.d': {
      margin: '0 auto 1rem',
      maxHeight: '15rem',
      maxWidth: '10rem',
      height: 'auto',
    },

    '.c': {
      width: '100%',
      maxWidth: '20rem',
      margin: '0 auto',
    },
  },
})

interface Props extends BlockButtonProps, HasCountdownProps {
  id?: string
  title?: TitleProps
  subtitle?: TitleProps
  background: BackgroundColor
  size?: 'm' | 'l'
  space?: Space
  backgroundColor?: string
  colorModifier?: TypographyColorModifier
  isSubtitleBold?: boolean
  analytics?: CustomAnalyticsAttributes
  /**
   * Midia Medusa - Preparação para receber props da migração
   */
  midia: MediaProps
}

const HeroProductBlock = ({
  id,
  title,
  subtitle,
  hasButton = false,
  button,
  buttonLabel,
  buttonType,
  buttonValue,
  buttonAriaLabel,
  midia: medusaMidiaProps,
  size = 'l',
  space = 'l',
  background,
  backgroundColor,
  colorModifier,
  hasCountdown,
  clockBackGround,
  clockBackGroundHex,
  labelColorModifier,
  clockColorModifier,
  countdownDate,
  countdownDay,
  isSubtitleBold = true,
  analytics,
}: Props) => {
  const TitleComponent = size === LIST_SIZE.lg ? Display : HeadingL
  const SubtitleComponent = isSubtitleBold ? SubHeading : BodyM

  const lightText = builderLightText(colorModifier)

  const containerProps: ContainerProps = {
    contentClassName: 'a',
    className: 'e',
    spaceOut: space,
    spaceIn: 'm',
    backgroundColor: background,
    backgroundColorHex: backgroundColor,
    id,
  }

  const buttonProps = {
    buttonLabel: buttonLabel || button?.label,
    buttonType: buttonType || button?.action?.type,
    buttonValue: buttonValue || button?.action?.value,
    buttonAriaLabel: buttonAriaLabel || button?.label,
  }

  const countdownProps = {
    clockBackGround,
    clockBackGroundHex,
    labelColorModifier,
    clockColorModifier,
    countdownTimestampMs: parseIsoToTimestampMS(countdownDate),
    daysFromInput: countdownDay || 2,
  }
  const customButtonEvent = extractCustomButtonEvent(analytics)

  return (
    <HeroProductBlockStyles>
      <Container {...containerProps}>
        <div className="b">
          {title && title.text && (
            <TitleComponent
              dark="high"
              light={lightText}
              className="eu-sm-2"
              tag={title.tag || 'h1'}
            >
              {title.text}
            </TitleComponent>
          )}

          {subtitle && subtitle.text && (
            <SubtitleComponent
              dark="medium"
              light={lightText}
              className="eu-sm-2"
              tag={subtitle.tag}
            >
              {subtitle.text}
            </SubtitleComponent>
          )}
          {hasCountdown && countdownProps && (
            <Countdown className="e eu-sm-2" {...countdownProps} />
          )}
        </div>

        <div className="c">
          {hasButton && buttonProps && (
            <Button
              event={customButtonEvent}
              className="e eu-sm-2"
              action={buttonProps.buttonType}
              value={decodeURL(buttonProps.buttonValue)}
              size="lg"
              modifier={lightText ? 'solid-invert' : 'solid'}
              aria-label={buttonProps.buttonAriaLabel}
              aria-hidden={!hasButton}
            >
              {buttonProps.buttonLabel}
            </Button>
          )}
        </div>

        <div className="d">
          <Media width={400} height={316} layout="responsive" priority {...medusaMidiaProps} />
        </div>
      </Container>
    </HeroProductBlockStyles>
  )
}

export default HeroProductBlock
